import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  deleteAdminV1StoresStoreIdMarketsMarketIdPricesVariantId,
  deleteAdminV1StoresStoreIdProductsId,
  getAdminV1OrgProductsIdEngagement,
  getAdminV1OrgSchedules,
  getAdminV1OrgSchedulesId,
  getAdminV1StoresStoreIdMarketsMarketIdPrices,
  getAdminV1StoresStoreIdProducts,
  getAdminV1StoresStoreIdProductsId,
  getAdminV1StoresStoreIdProductsIdPricing,
  patchAdminV1StoresStoreIdProductsId,
  postAdminV1StoresStoreIdProducts,
  putAdminV1StoresStoreIdMarketsMarketIdPrices,
  putAdminV1StoresStoreIdProductsBulkPublish,
  putAdminV1StoresStoreIdVariantsPricingBulk,
  putAdminV1StoresStoreIdVariantsVariantIdPricing,
  type BulkPublishResponse,
  type GetAdminV1OrgSchedules200,
  type GetAdminV1StoresStoreIdMarketsMarketIdPrices200,
  type StoreProductListPage,
  type MarketPrice,
  type ProductEngagement,
  type ProductSchedule,
  type PatchAdminV1StoresStoreIdProductsIdBody,
  type PostAdminV1StoresStoreIdProductsBody,
  type PutAdminV1StoresStoreIdMarketsMarketIdPricesBody,
  type PutAdminV1StoresStoreIdProductsBulkPublishBody,
  type PutAdminV1StoresStoreIdVariantsPricingBulkBody,
  type PutAdminV1StoresStoreIdVariantsVariantIdPricingBody,
  type StoreProduct,
  type StoreVariantPricing,
} from '@nucleus-commerce/api-client/admin';

/**
 * List params as `listParams` builds them from the route's search: `q`, `sort`, the flat filter
 * params (`status=draft,published`, `publishedAt_gte=2026-09-01`, `productId`), `cursor`, `limit`.
 */
export type StoreProductListParams = ListParams;

/** Publication of the master catalog into one store, and the store's variant prices. */
export const storeCatalogApi = {
  list: (storeId: string, params: StoreProductListParams): Promise<StoreProductListPage> =>
    getAdminV1StoresStoreIdProducts({ storeId }, params),
  get: (storeId: string, id: string): Promise<StoreProduct> =>
    getAdminV1StoresStoreIdProductsId({ storeId, id }),
  create: (storeId: string, body: PostAdminV1StoresStoreIdProductsBody): Promise<StoreProduct> =>
    postAdminV1StoresStoreIdProducts({ storeId }, body),
  update: (
    storeId: string,
    id: string,
    body: PatchAdminV1StoresStoreIdProductsIdBody,
  ): Promise<StoreProduct> => patchAdminV1StoresStoreIdProductsId({ storeId, id }, body),
  remove: (storeId: string, id: string): Promise<void> =>
    deleteAdminV1StoresStoreIdProductsId({ storeId, id }),
  bulkPublish: (
    storeId: string,
    body: PutAdminV1StoresStoreIdProductsBulkPublishBody,
  ): Promise<BulkPublishResponse> => putAdminV1StoresStoreIdProductsBulkPublish({ storeId }, body),

  pricing: (storeId: string, id: string): Promise<StoreVariantPricing[]> =>
    getAdminV1StoresStoreIdProductsIdPricing({ storeId, id }).then((r) => r.items),
  bulkPricing: (
    storeId: string,
    body: PutAdminV1StoresStoreIdVariantsPricingBulkBody,
  ): Promise<StoreVariantPricing[]> =>
    putAdminV1StoresStoreIdVariantsPricingBulk({ storeId }, body).then((r) => r.items),
  variantPricing: (
    storeId: string,
    variantId: string,
    body: PutAdminV1StoresStoreIdVariantsVariantIdPricingBody,
  ): Promise<StoreVariantPricing> =>
    putAdminV1StoresStoreIdVariantsVariantIdPricing({ storeId, variantId }, body),

  marketPricesPage: (
    storeId: string,
    marketId: string,
    params: { productId: string; cursor?: string },
  ): Promise<GetAdminV1StoresStoreIdMarketsMarketIdPrices200> =>
    getAdminV1StoresStoreIdMarketsMarketIdPrices({ storeId, marketId }, { ...params, limit: 100 }),
  setMarketPrices: (
    storeId: string,
    marketId: string,
    body: PutAdminV1StoresStoreIdMarketsMarketIdPricesBody,
  ): Promise<MarketPrice[]> =>
    putAdminV1StoresStoreIdMarketsMarketIdPrices({ storeId, marketId }, body).then((r) => r.items),
  removeMarketPrice: (storeId: string, marketId: string, variantId: string): Promise<void> =>
    deleteAdminV1StoresStoreIdMarketsMarketIdPricesVariantId({ storeId, marketId, variantId }),

  /**
   * Rotations for the list's schedule filter: a first page of the ones targeting this store that
   * have not ended (an ended rotation owns nothing), matching the picker's text.
   */
  schedules: (storeId: string, q: string): Promise<GetAdminV1OrgSchedules200> =>
    getAdminV1OrgSchedules({
      storeId,
      status: 'draft,active,paused',
      ...(q ? { q } : {}),
      limit: 20,
    }),
  schedule: (id: string): Promise<ProductSchedule> => getAdminV1OrgSchedulesId({ id }),

  /** One master product's views, carts, wishlists and sales per store over its last `days` days. */
  productEngagement: (productId: string, days: number): Promise<ProductEngagement> =>
    getAdminV1OrgProductsIdEngagement({ id: productId }, { days }),
};
